/* ============================================
   Sticky Buy Button — FINAL FIXED
   assets/sticky-buy-button.js
   ============================================ */

(function () {
  'use strict';

  class StickyBuy {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.blockId = root.dataset.blockId;
      this.productId = root.dataset.productId;
      this.discountPercent = Number(root.dataset.discountPercent || 0);
      this.stickyEnabled = root.dataset.stickyEnabled !== 'false';
      this.cartType = root.dataset.cartType || 'drawer';

      this.sentinel = root.querySelector('[data-sentinel]');
      this.inner = root.querySelector('[data-inner]');
      this.form = root.querySelector('form');
      this.btn = root.querySelector('[data-add-btn]');
      this.errorEl = root.querySelector('[data-error]');

      this.qtyInput = root.querySelector('[data-qty-input]');
      this.qtyMinus = root.querySelector('[data-qty-minus]');
      this.qtyPlus = root.querySelector('[data-qty-plus]');

      this.priceEl = root.querySelector('[data-btn-price]');
      this.variantInput = root.querySelector('[data-variant-id]');

      this.busy = false;
      this.scrollTicking = false;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.cartObservers = [];

      this.init();
    }

    init() {
      this.bindQty();
      this.bindForm();
      this.bindVariantChange();
      this.bindSticky();
      this.bindCartDrawerVisibility();
      this.syncFromCurrentVariant();
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    bindSticky() {
      if (!this.stickyEnabled) {
        this.root.dataset.stuck = 'false';
        return;
      }

      this.updateStickyState();

      window.addEventListener('scroll', () => this.requestStickyUpdate(), { passive: true });
      window.addEventListener('resize', () => this.updateStickyState());

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', () => this.updateStickyState());
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(() => this.updateStickyState());
      }

      document.addEventListener('shopify:section:load', () => {
        window.setTimeout(() => this.updateStickyState(), 100);
      });

      document.addEventListener('shopify:block:select', () => {
        window.setTimeout(() => this.updateStickyState(), 100);
      });
    }

    requestStickyUpdate() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        this.updateCartOpenState();
        this.updateStickyState();
        this.scrollTicking = false;
      });
    }

    updateStickyState() {
      if (!this.stickyEnabled || !this.isMobile()) {
        this.root.dataset.stuck = 'false';
        return;
      }

      if (this.root.dataset.cartOpen === 'true') {
        this.root.dataset.stuck = 'false';
        return;
      }

      const rootTop = this.root.getBoundingClientRect().top + window.scrollY;
      const shouldStick = window.scrollY > rootTop - 10;

      this.root.dataset.stuck = shouldStick ? 'true' : 'false';
    }

    getCartDrawers() {
      return Array.from(
        document.querySelectorAll(
          'cart-drawer, #CartDrawer, .cart-drawer, .drawer--cart, cart-notification'
        )
      ).filter(Boolean);
    }

    isElementVisible(element) {
      if (!element) return false;

      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();

      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0
      );
    }

    isCartOpen() {
      const drawers = this.getCartDrawers();

      for (const drawer of drawers) {
        const ariaHidden = drawer.getAttribute('aria-hidden');
        const classes = drawer.classList;

        if (ariaHidden === 'true') continue;

        const drawerLooksOpen =
          drawer.hasAttribute('open') ||
          ariaHidden === 'false' ||
          classes.contains('active') ||
          classes.contains('is-active') ||
          classes.contains('open') ||
          classes.contains('drawer--is-open');

        if (drawerLooksOpen && this.isElementVisible(drawer)) {
          return true;
        }
      }

      return false;
    }

    updateCartOpenState() {
      const cartOpen = this.isCartOpen();

      this.root.dataset.cartOpen = cartOpen ? 'true' : 'false';

      if (cartOpen) {
        this.root.dataset.stuck = 'false';
      }
    }

    bindCartDrawerVisibility() {
      const updateSoon = (delay = 80) => {
        window.setTimeout(() => {
          this.updateCartOpenState();
          this.updateStickyState();
        }, delay);
      };

      const observeDrawers = () => {
        this.getCartDrawers().forEach((drawer) => {
          if (drawer.__stickyBuyCartObserved) return;

          drawer.__stickyBuyCartObserved = true;

          const drawerObserver = new MutationObserver(() => {
            updateSoon(80);
            updateSoon(350);
          });

          drawerObserver.observe(drawer, {
            attributes: true,
            attributeFilter: ['class', 'style', 'open', 'aria-hidden'],
            childList: true,
            subtree: true
          });

          drawer.addEventListener('transitionend', () => updateSoon(50));
          drawer.addEventListener('animationend', () => updateSoon(50));

          this.cartObservers.push(drawerObserver);
        });
      };

      const bodyObserver = new MutationObserver(() => {
        observeDrawers();
        updateSoon(80);
        updateSoon(350);
      });

      bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true
      });

      this.cartObservers.push(bodyObserver);

      observeDrawers();
      this.updateCartOpenState();

      document.addEventListener(
        'click',
        () => {
          observeDrawers();
          updateSoon(80);
          updateSoon(350);
          updateSoon(800);
        },
        true
      );

      document.addEventListener(
        'keyup',
        () => {
          updateSoon(80);
          updateSoon(350);
        },
        true
      );
    }

    bindQty() {
      if (!this.qtyInput) return;

      const getMin = () => parseInt(this.qtyInput.min, 10) || 1;
      const getMax = () => parseInt(this.qtyInput.max, 10) || 99;

      const clamp = (value) => {
        const min = getMin();
        const max = getMax();

        return Math.max(min, Math.min(max, value));
      };

      if (this.qtyMinus) {
        this.qtyMinus.addEventListener('click', () => {
          const current = parseInt(this.qtyInput.value, 10) || getMin();

          this.qtyInput.value = clamp(current - 1);
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      if (this.qtyPlus) {
        this.qtyPlus.addEventListener('click', () => {
          const current = parseInt(this.qtyInput.value, 10) || getMin();

          this.qtyInput.value = clamp(current + 1);
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      this.qtyInput.addEventListener('change', () => {
        const current = parseInt(this.qtyInput.value, 10) || getMin();

        this.qtyInput.value = clamp(current);
      });
    }

    findMainVariantInput() {
      const productInfo =
        document.querySelector(`product-info[data-product-id="${this.productId}"]`) ||
        document.querySelector('product-info');

      if (productInfo) {
        const productInfoInput = productInfo.querySelector('input[name="id"]');

        if (productInfoInput && !this.root.contains(productInfoInput) && productInfoInput.value) {
          return productInfoInput;
        }
      }

      const inputs = Array.from(document.querySelectorAll('input[name="id"]'));

      return (
        inputs.find((input) => {
          if (!input.value) return false;
          if (this.root.contains(input)) return false;
          if (input.closest('.custom-upsell-carousel')) return false;
          if (input.closest('.sticky-buy')) return false;

          return true;
        }) ||
        this.variantInput ||
        null
      );
    }

    async syncFromCurrentVariant() {
      const mainInput = this.findMainVariantInput();
      const variantId = mainInput?.value || this.variantInput?.value;

      if (!variantId) return;

      await this.syncVariantById(variantId);
    }

    bindVariantChange() {
      document.addEventListener('variant:change', (event) => {
        this.syncVariant(event.detail);
      });

      const mainVariantInput = this.findMainVariantInput();

      if (mainVariantInput) {
        const observer = new MutationObserver(() => {
          this.syncVariantById(mainVariantInput.value);
        });

        observer.observe(mainVariantInput, {
          attributes: true,
          attributeFilter: ['value']
        });

        mainVariantInput.addEventListener('change', () => {
          this.syncVariantById(mainVariantInput.value);
        });
      }

      document.addEventListener('change', (event) => {
        const target = event.target;

        if (!target || this.root.contains(target)) return;

        const isVariantControl =
          target.matches('input[type="radio"]') ||
          target.matches('select') ||
          target.matches('variant-selects select') ||
          target.matches('variant-radios input');

        if (!isVariantControl) return;

        window.setTimeout(() => {
          this.syncFromCurrentVariant();
        }, 150);
      });
    }

    syncVariant(detail) {
      if (!detail || !detail.variant) return;

      const variant = detail.variant;

      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      this.renderPrice(variant);

      this.updateAvailability(variant.available);
    }

    async syncVariantById(variantId) {
      if (!variantId || !this.variantInput) return;

      this.variantInput.value = variantId;

      try {
        const handle = this.getProductHandle();

        if (!handle) return;

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);

        if (!response.ok) return;

        const product = await response.json();
        const variant = product.variants.find((item) => String(item.id) === String(variantId));

        if (!variant) return;

        this.renderPrice(variant);

        this.updateAvailability(variant.available);
      } catch (error) {
        /* silent */
      }
    }

    updateAvailability(isAvailable) {
      if (!this.btn) return;

      const label = this.btn.querySelector('[data-btn-label]');
      const price = this.btn.querySelector('[data-btn-price]');

      const defaultLabel = this.btn.dataset.defaultLabel || 'ДОДАТИ В КОШИК';
      const soldoutLabel = this.btn.dataset.soldoutLabel || 'НЕМАЄ В НАЯВНОСТІ';
      const showPriceWhenSoldout = this.btn.dataset.showPriceWhenSoldout === 'true';

      if (isAvailable) {
        this.btn.disabled = false;
        this.btn.removeAttribute('data-unavailable');

        if (label) label.textContent = defaultLabel;
        if (price) price.hidden = false;

        return;
      }

      this.btn.disabled = true;
      this.btn.dataset.unavailable = 'true';
      this.btn.removeAttribute('data-state');

      if (label) label.textContent = soldoutLabel;
      if (price) price.hidden = !showPriceWhenSoldout;
    }

    getProductHandle() {
      const match = window.location.pathname.match(/\/products\/([^/?]+)/);

      return match ? match[1] : '';
    }

    renderPrice(variant) {
      if (!this.priceEl || !variant || variant.price == null) return;

      const discountPercent = Number(this.discountPercent || 0);

      if (discountPercent > 0) {
        const discountedPrice = Math.round(
          Number(variant.price) * (100 - discountPercent) / 100
        );

        this.priceEl.innerHTML = `
          <span class="sticky-buy__btn-compare-price">
            ${this.formatMoney(variant.price)}
          </span>
          <span class="sticky-buy__btn-current-price">
            ${this.formatMoney(discountedPrice)}
          </span>
        `;
        return;
      }

      this.priceEl.innerHTML = `
        <span class="sticky-buy__btn-current-price">
          ${this.formatMoney(variant.price)}
        </span>
      `;
    }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
        }

        const amount = (cents / 100).toFixed(0);

        return `${amount}₴`;
        }
    bindForm() {
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart();
      });
    }

    async addToCart() {
      if (this.busy || !this.btn || this.btn.disabled) return;

      this.busy = true;
      this.hideError();
      this.setState('loading');

      const formData = new FormData(this.form);
      const sections = this.getSectionsToRender();

      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Не вдалося додати в кошик');
        }

        this.setState('success');

        try {
          this.updateCartUI(data);
          this.openCart();
        } catch (cartError) {
          /* товар добавлен, ошибку drawer не показываем */
        }

        window.setTimeout(() => {
          this.setState('default');
          this.busy = false;
          this.updateCartOpenState();
          this.updateStickyState();
        }, 1600);
      } catch (error) {
        this.setState('default');
        this.showError(error.message || 'Сталася помилка');
        this.busy = false;
      }
    }

    getSectionsToRender() {
      const sections = [];

      if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
      if (document.querySelector('cart-notification')) sections.push('cart-notification');
      if (document.querySelector('#cart-icon-bubble')) sections.push('cart-icon-bubble');

      return sections;
    }

    updateCartUI(data) {
      if (!data.sections) return;

      Object.entries(data.sections).forEach(([sectionId, html]) => {
        if (!html) return;

        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, 'text/html');

        if (sectionId === 'cart-icon-bubble') {
          const target = document.querySelector('#cart-icon-bubble');
          const fresh = newDoc.querySelector('#shopify-section-cart-icon-bubble');

          if (target && fresh) target.innerHTML = fresh.innerHTML;
        }

        if (sectionId === 'cart-drawer') {
          const target = document.querySelector('cart-drawer');
          const fresh = newDoc.querySelector('cart-drawer');

          if (target && fresh) {
            target.innerHTML = fresh.innerHTML;
          }
        }

        if (sectionId === 'cart-notification') {
          const target = document.querySelector('cart-notification');
          const fresh = newDoc.querySelector('cart-notification');

          if (target && fresh) {
            target.innerHTML = fresh.innerHTML;
          }
        }
      });
    }

    openCart() {
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('#CartDrawer') ||
        document.querySelector('.cart-drawer');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        window.setTimeout(() => this.updateCartOpenState(), 80);
        return;
      }

      if (drawer) {
        drawer.classList.add('active', 'animate', 'is-active', 'open');
        drawer.setAttribute('aria-hidden', 'false');
        window.setTimeout(() => this.updateCartOpenState(), 80);
        return;
      }

      const notification = document.querySelector('cart-notification');

      if (notification && typeof notification.open === 'function') {
        notification.open();
        window.setTimeout(() => this.updateCartOpenState(), 80);
        return;
      }

      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    }

      setState(state) {
  if (!this.btn) return;

  const label = this.btn.querySelector('[data-btn-label]');
  const defaultLabel = this.btn.dataset.defaultLabel || 'Додати в кошик';
  const loadingLabel = this.btn.dataset.loadingLabel || 'Додаємо...';
  const successLabel = this.btn.dataset.successLabel || 'Додано';

  if (state === 'default') {
    this.btn.removeAttribute('data-state');
    if (label) label.textContent = defaultLabel;
    return;
  }

  this.btn.dataset.state = state;

  if (label) {
    if (state === 'loading') label.textContent = loadingLabel;
    if (state === 'success') label.textContent = successLabel;
  }
}
    showError(message) {
      if (!this.errorEl) return;

      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;

      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  function initStickyBuy(container) {
    const scope = container || document;

    scope.querySelectorAll('.sticky-buy').forEach((element) => {
      if (element.__stickyBuyInit) return;

      element.__stickyBuyInit = true;
      new StickyBuy(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStickyBuy(document));
  } else {
    initStickyBuy(document);
  }

  document.addEventListener('shopify:section:load', (event) => initStickyBuy(event.target));
  document.addEventListener('shopify:block:select', (event) => initStickyBuy(event.target));
})();